import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { MessageSquare, X, Send, Sparkles, Plus, Trash2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * Floating AI chatbot — bottom right corner.
 * - Opens a compact chat panel with role-scoped answers about CRM data.
 * - Persists sessions per user in Mongo.
 * - Multi-turn conversation with session_id.
 */

const DEFAULT_SUGGESTIONS = [
  "What are our sales this month?",
  "Top 5 affiliates this month",
  "Active affiliate %?",
  "Pending payout total",
];

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1" data-testid="chatbot-typing">
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "240ms" }} />
    </div>
  );
}

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-4 space-y-0.5 my-1">
          {listBuf.map((l, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(l) }} />
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^\s*[-•]\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*[-•]\s+/, ""));
    } else {
      flushList();
      if (line === "") {
        out.push(<div key={`sp-${idx}`} className="h-1" />);
      } else {
        out.push(
          <p key={idx} className="my-0.5" dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
        );
      }
    }
  });
  flushList();
  return out;
}

function formatInline(s) {
  // Very small allowlist: **bold**, `code`
  const esc = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-secondary text-[11px]">$1</code>');
}

export function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0 && !sessionId) {
      // Fresh session — show greeting
      setMessages([
        {
          role: "assistant",
          content:
            "Hey! I'm your Fuelled Copilot. Ask me about affiliates, sales, tiers, payouts, or the reports — I'll pull live data for you.",
        },
      ]);
    }
  }, [open, messages.length, sessionId]);

  useEffect(() => {
    if (open) {
      // load user's recent sessions
      api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const startNew = () => {
    setSessionId(null);
    setMessages([]);
    setShowHistory(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/chat/sessions/${sid}`);
      setSessionId(sid);
      setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      setShowHistory(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${sid}`);
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (sid === sessionId) startNew();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const nextMessages = [...messages, { role: "user", content: msg }];
    setMessages(nextMessages);
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", {
        message: msg,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages([...nextMessages, { role: "assistant", content: data.response }]);
      // refresh session list
      api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
    } catch (e) {
      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content:
            "Sorry — I couldn't answer that just now. " + formatApiError(e),
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="chatbot-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
          aria-label="Open AI Copilot"
        >
          <Sparkles className="h-6 w-6" strokeWidth={2.2} />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-1.5rem)] h-[560px] max-h-[calc(100vh-3rem)] rounded-2xl border border-border/60 bg-background shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-card/60 backdrop-blur">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold tracking-tight leading-none">Fuelled Copilot</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  AI · Live CRM data
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="chatbot-history-btn"
                onClick={() => setShowHistory((v) => !v)}
                className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="Chat history"
              >
                <MessageSquare className="h-4 w-4" />
              </button>
              <button
                data-testid="chatbot-new-btn"
                onClick={startNew}
                className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="New chat"
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                data-testid="chatbot-close-btn"
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* History drawer */}
          {showHistory && (
            <div
              data-testid="chatbot-history-panel"
              className="absolute inset-x-0 top-14 z-10 bg-background border-b border-border/60 max-h-[280px] overflow-y-auto"
            >
              {sessions.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground">No previous chats yet.</div>
              ) : (
                <ul className="py-1">
                  {sessions.map((s) => (
                    <li key={s.id}>
                      <button
                        data-testid={`chatbot-session-${s.id}`}
                        onClick={() => loadSession(s.id)}
                        className={cn(
                          "w-full flex items-center justify-between gap-2 px-4 py-2 text-left text-xs hover:bg-secondary transition-colors",
                          s.id === sessionId && "bg-primary/5"
                        )}
                      >
                        <span className="truncate flex-1">{s.title}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => deleteSession(s.id, e)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") deleteSession(s.id, e);
                          }}
                          className="opacity-60 hover:opacity-100 hover:text-destructive transition-opacity cursor-pointer"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${m.role}`}
                className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed",
                    m.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : m.error
                      ? "bg-destructive/10 text-destructive"
                      : "bg-secondary/70 text-foreground"
                  )}
                >
                  {m.role === "assistant" ? renderMarkdown(m.content) : <span>{m.content}</span>}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-secondary/70 rounded-2xl px-3.5 py-2.5">
                  <TypingDots />
                </div>
              </div>
            )}
          </div>

          {/* Suggestions (only on empty chat) */}
          {messages.length <= 1 && !sending && (
            <div data-testid="chatbot-suggestions" className="px-4 pb-2 flex flex-wrap gap-1.5">
              {DEFAULT_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`chatbot-suggestion-${s.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => send(s)}
                  className="text-[11px] rounded-full border border-border/60 bg-secondary/40 hover:bg-secondary px-2.5 py-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            className="p-3 border-t border-border/60 bg-card/30"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask about affiliates, sales, tiers…"
                rows={1}
                className="flex-1 resize-none rounded-xl border border-border/60 bg-background px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 max-h-[100px]"
                disabled={sending}
              />
              <button
                type="submit"
                data-testid="chatbot-send-btn"
                disabled={sending || !input.trim()}
                className="h-9 w-9 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                aria-label="Send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
